"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StyledTooltip = exports.TooltipTrigger = exports.Tooltip = exports.TooltipTriggerContext = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_1 = __importStar(require("react"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../theme");
// Create the context for tooltip trigger detection
exports.TooltipTriggerContext = (0, react_1.createContext)({ isTooltipTrigger: false });
/**
 * A tooltip component that displays additional information when users hover over or focus on a trigger element.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/Tooltip.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-tooltip--default}
 *
 * @example
 * ```tsx
 * <TooltipTrigger>
 *   <PrimaryButton>Hover me</PrimaryButton>
 *   <Tooltip
 *     placement="right"
 *     delay={1000}
 *     closeDelay={300}
 *   >
 *     This is a tooltip
 *   </Tooltip>
 * </TooltipTrigger>
 * ```
 *
 * @param triggerRef - The ref for the element which the tooltip positions itself with respect to
 * @param isEntering - Whether the tooltip is currently performing an entry animation
 * @param isExiting - Whether the tooltip is currently performing an exit animation
 * @param placement - The placement of the tooltip with respect to the trigger. Defaults to 'top'
 * @param containerPadding - The placement padding between the element and its container. Defaults to 12
 * @param offset - The additional offset along the main axis between the element and its anchor
 * @param crossOffset - The additional offset along the cross axis between the element and its anchor
 * @param shouldFlip - Whether the element should flip its orientation when there is insufficient room. Defaults to true
 * @param isOpen - Whether the element is rendered
 * @param arrowBoundaryOffset - The minimum distance the arrow's edge should be from the overlay edge
 * @param defaultOpen - Whether the overlay is open by default (uncontrolled)
 * @param children - The children of the component. Can be ReactNode or a render function
 * @param className - The CSS className for the element. Can be string or a function
 * @param style - The CSS styles for the element. Can be CSSProperties or a function
 * @param onOpenChange - Handler that is called when the overlay's open state changes
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
var Tooltip = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (react_1.default.createElement(exports.StyledTooltip, __assign({}, props),
        react_1.default.createElement(react_aria_components_1.OverlayArrow, null,
            react_1.default.createElement("svg", { fill: "currentColor", width: 8, height: 8, viewBox: "0 0 8 8" },
                react_1.default.createElement("path", { d: "M0 0 L4 4 L8 0" }))),
        children));
};
exports.Tooltip = Tooltip;
/**
 * A trigger component that controls when a tooltip is displayed.
 * The tooltip will appear when users hover over or focus on the trigger element.
 *
 * @example
 * ```tsx
 * <TooltipTrigger
 *   delay={1500}
 *   closeDelay={500}
 *   trigger="focus"
 * >
 *   <PrimaryButton>Focus me</PrimaryButton>
 *   <Tooltip>This tooltip appears on focus</Tooltip>
 * </TooltipTrigger>
 * ```
 *
 * @param children - The children of the component
 * @param isDisabled - Whether the tooltip should be disabled, independent from the trigger
 * @param delay - The delay time for the tooltip to show up. Defaults to 1500ms
 * @param closeDelay - The delay time for the tooltip to close. Defaults to 500ms
 * @param trigger - By default, opens for both focus and hover. Can be made to open only for focus
 * @param isOpen - Whether the overlay is open by default (controlled)
 * @param defaultOpen - Whether the overlay is open by default (uncontrolled)
 * @param onOpenChange - Handler that is called when the overlay's open state changes
 */
var TooltipTrigger = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (react_1.default.createElement(exports.TooltipTriggerContext.Provider, { value: { isTooltipTrigger: true } },
        react_1.default.createElement(react_aria_components_1.TooltipTrigger, __assign({}, props), children)));
};
exports.TooltipTrigger = TooltipTrigger;
exports.StyledTooltip = (0, styled_1.default)(react_aria_components_1.Tooltip)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  box-shadow: 0 ", " ", " rgba(0.11 0.153 0.227 / 0.1);\n  border-radius: ", ";\n  background: ", ";\n  color: ", ";\n  forced-color-adjust: none;\n  outline: none;\n  padding: ", ";\n  max-width: 380px;\n  /* fixes FF gap */\n  transform: translate3d(0, 0, 0);\n\n  &[data-placement='top'] {\n    margin-bottom: ", ";\n  }\n\n  &[data-placement='bottom'] {\n    margin-top: ", ";\n    & .react-aria-OverlayArrow svg {\n      transform: rotate(180deg);\n    }\n  }\n\n  &[data-placement='right'] {\n    margin-left: ", ";\n    & .react-aria-OverlayArrow svg {\n      transform: rotate(90deg);\n    }\n  }\n  &[data-placement='left'] {\n    margin-right: ", ";\n    & .react-aria-OverlayArrow svg {\n      transform: rotate(-90deg);\n    }\n  }\n\n  & .react-aria-OverlayArrow svg {\n    display: block;\n    color: ", ";\n    height: ", ";\n  }\n}\n"], ["\n  box-shadow: 0 ", " ", " rgba(0.11 0.153 0.227 / 0.1);\n  border-radius: ", ";\n  background: ", ";\n  color: ", ";\n  forced-color-adjust: none;\n  outline: none;\n  padding: ", ";\n  max-width: 380px;\n  /* fixes FF gap */\n  transform: translate3d(0, 0, 0);\n\n  &[data-placement='top'] {\n    margin-bottom: ", ";\n  }\n\n  &[data-placement='bottom'] {\n    margin-top: ", ";\n    & .react-aria-OverlayArrow svg {\n      transform: rotate(180deg);\n    }\n  }\n\n  &[data-placement='right'] {\n    margin-left: ", ";\n    & .react-aria-OverlayArrow svg {\n      transform: rotate(90deg);\n    }\n  }\n  &[data-placement='left'] {\n    margin-right: ", ";\n    & .react-aria-OverlayArrow svg {\n      transform: rotate(-90deg);\n    }\n  }\n\n  & .react-aria-OverlayArrow svg {\n    display: block;\n    color: ", ";\n    height: ", ";\n  }\n}\n"])), theme_1.theme.space.space8, theme_1.theme.space.space16, theme_1.theme.radii.field, theme_1.theme.colors.bgOverlayInverse, theme_1.theme.colors.contentInverse, theme_1.theme.space.space12, theme_1.theme.space.space8, theme_1.theme.space.space8, theme_1.theme.space.space8, theme_1.theme.space.space8, theme_1.theme.colors.bgOverlayInverse, theme_1.theme.space.space8);
var templateObject_1;
//# sourceMappingURL=Tooltip.js.map